/**
 * useProjectFilterParam — shared `?project=<id>` filter for list pages
 * (Calendars, Campaigns, Forecasts, Content analysis, Off-Page Briefs).
 *
 * One convention everywhere:
 *   - The URL is the single source of truth → filtered views are shareable
 *     and Back/Forward work.
 *   - localStorage (per-page key) only seeds a first-visit default; it never
 *     rewrites the URL afterwards, so a deliberately-shared bare link stays
 *     bare and Back is never trapped by a sync effect.
 *   - Writes use `replace` (filter tweaks shouldn't pile up history entries)
 *     and preserve any other validated params on the route (e.g. the
 *     campaigns `tab`).
 */
import { useEffect, useRef } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import type { Project } from '@seo/types';

export interface ProjectFilterOptions {
  /** Per-page localStorage key, e.g. `seo:calendar-project`. */
  storageKey: string;
  projects: Project[];
  projectsLoading: boolean;
  /**
   * When false (default) an empty selection means "All projects".
   * When true and there is exactly one project, it is auto-selected.
   */
  autoSelectSingle?: boolean;
}

export function useProjectFilterParam({
  storageKey,
  projects,
  projectsLoading,
  autoSelectSingle = true,
}: ProjectFilterOptions): {
  projectId: string;
  setProjectId: (v: string) => void;
} {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { project?: string };
  const projectId = search.project ?? '';

  const setProjectId = (v: string): void => {
    try {
      if (v) window.localStorage.setItem(storageKey, v);
      else window.localStorage.removeItem(storageKey);
    } catch {
      /* ignore */
    }
    void navigate({
      // `project: undefined` deletes the param — never a stale leftover.
      search: (prev) =>
        ({ ...(prev as object), project: v || undefined }) as never,
      replace: true,
    });
  };

  // First-visit default, exactly once per mount.
  const seededRef = useRef(false);
  useEffect(() => {
    if (seededRef.current) return;
    if (projectsLoading) return;
    seededRef.current = true;
    if (search.project) return;
    let stored: string | null = null;
    try {
      stored = window.localStorage.getItem(storageKey);
    } catch {
      /* ignore */
    }
    const candidate =
      (stored && projects.some((p) => p.id === stored) ? stored : null) ??
      (autoSelectSingle && projects.length === 1 ? projects[0]!.id : null);
    if (candidate) {
      void navigate({
        search: (prev) => ({ ...(prev as object), project: candidate }) as never,
        replace: true,
      });
    }
  }, [
    projectsLoading,
    projects,
    search.project,
    storageKey,
    autoSelectSingle,
    navigate,
  ]);

  return { projectId, setProjectId };
}
